// Stale-tab detection. Railway auto-deploys on every push to main, so a
// browser tab left open keeps running an old bundle whose hashed chunks may
// 404 — clicks then die silently. This hook polls the unauthenticated
// /health endpoint (which reports the SERVER's build SHA) and compares it to
// the SHA THIS bundle was built from (`__BUILD_SHA__`, embedded by Vite). When
// they diverge, a newer version is live and we surface a refresh banner.
//
// House pattern: this uses TanStack Query `useQuery` with `refetchInterval` +
// `refetchOnWindowFocus`, matching the established polling precedent across the
// app (ProductionTilesPage, CommandCenterDashboardPage, SalesDashboardPage,
// etc. all use useQuery + refetchInterval). TanStack's focusManager fires on
// BOTH window focus AND `visibilitychange`, so returning to a long-open tab
// triggers a check. The pure decision (`shouldShowUpdateBanner`) stays in its
// own gate-executed module — it is NOT reimplemented here.
//
// Conservative by construction:
//   • `enabled: MY_BUILD !== null` → FULLY INERT in dev / any build without an
//     embedded SHA (no query, no fetch, banner never shows).
//   • `retry: false` + we swallow errors into a null result → a flaky /health
//     never nags and never spins.
//   • `staleTime` throttles focus refetches (the idiomatic replacement for the
//     old hand-rolled 60s gate), while `refetchInterval` drives the background
//     ~5-min poll.
//   • Both `refetchInterval` and `refetchOnWindowFocus` return false once a
//     mismatch is confirmed → we STOP POLLING for good (the answer can't change
//     until the user reloads).
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { shouldShowUpdateBanner } from "@/lib/versionCheck";

const HEALTH_URL = "/health";
const POLL_INTERVAL_MS = 5 * 60 * 1000; // background re-check
const FOCUS_THROTTLE_MS = 60 * 1000; // at most one focus-driven fetch / minute

// The SHA this bundle was built from, or null in dev/preview (feature inert).
// Resolved once at module load: `__BUILD_SHA__` is a Vite compile-time
// constant, so `typeof` is a literal check the bundler can even fold away.
const MY_BUILD: string | null =
  typeof __BUILD_SHA__ === "string" && __BUILD_SHA__ !== "" ? __BUILD_SHA__ : null;

// Dismiss persists for the session (module scope): once the user closes the
// banner it stays closed until the tab is reloaded/replaced. Kept simple on
// purpose — a reload is the whole point, so "hide for the session" is enough.
let sessionDismissed = false;

interface HealthResponse {
  build?: unknown;
}

export interface VersionCheck {
  /** True once a newer server build is confirmed AND the user hasn't dismissed. */
  showBanner: boolean;
  /** Hide the banner for the rest of this browser session. */
  dismiss: () => void;
}

/** Fetch the server's current build SHA; null when it can't be determined. */
async function fetchServerBuild(): Promise<string | null> {
  const res = await fetch(HEALTH_URL, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) return null; // server hiccup — treat as "no news", never nag
  const data = (await res.json()) as HealthResponse;
  return typeof data.build === "string" ? data.build : null;
}

/** Have we confirmed a newer server build than the one this tab is running? */
function isStale(serverBuild: string | null | undefined): boolean {
  return shouldShowUpdateBanner(MY_BUILD, serverBuild ?? null);
}

export function useVersionCheck(): VersionCheck {
  const [dismissed, setDismissed] = useState(sessionDismissed);

  const { data: serverBuild } = useQuery<string | null>({
    queryKey: ["version-check", "health-build"],
    queryFn: fetchServerBuild,
    // Inert in dev (no embedded SHA); on Railway this runs.
    enabled: MY_BUILD !== null,
    // Background poll — but stop the moment a newer build is confirmed.
    refetchInterval: (query) =>
      isStale(query.state.data) ? false : POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    // Re-check when the rep returns to the tab (focus + visibilitychange),
    // but stop once stale — nothing left to learn until they reload.
    refetchOnWindowFocus: (query) => !isStale(query.state.data),
    // A /health flake must never surface an error, nag, or spin.
    retry: false,
    // Throttle focus-driven refetches (idiomatic replacement for the old
    // manual 60s rate-limit).
    staleTime: FOCUS_THROTTLE_MS,
  });

  const stale = isStale(serverBuild);

  return {
    showBanner: stale && !dismissed,
    dismiss: () => {
      sessionDismissed = true;
      setDismissed(true);
    },
  };
}
